<template>
  <v-dialog
    v-if="locationData"
    v-model="modalState"
    width="700"
    persistent
    retain-focus
  >
    <v-card>
      <v-form v-model="formValid">
        <v-card-title
          ref="locationTitle"
          class="pb-0"
        >
          Location
        </v-card-title>

        <NoteSection
          label="Select options below to specify location"
        >
          <help-icon
            title="ICD-10 Requirements"
            body="<p>ICD-10 requires greater specificity of locations. Please be as detailed as possible.</p>"
          ></help-icon>

          <auto-complete-box
            v-model="locationData.location"
            :items="sortedLocations"
            label="Location"
            class="col-sm-6"
            :required="$custom.isEmpty(locationData.optional_modifier) ? true : false"
            @input="locationChanged"
          ></auto-complete-box>

          <auto-complete-box
            v-if="locationData.location === 1"
            v-model="locationData.section"
            :items="sectionOptions"
            label="Section"
            class="col-sm-6"
            multiple
            :required="$custom.isEmpty(locationData.optional_modifier) ? true : false"
          ></auto-complete-box>
          <auto-complete-box
            v-else-if="locationData.location === 3"
            v-model="locationData.quadrant"
            :items="quadrantOptions"
            label="Quadrant"
            class="col-sm-6"
            :required="$custom.isEmpty(locationData.optional_modifier) ? true : false"
          ></auto-complete-box>
          <auto-complete-box
            v-else
            v-model="locationData.left_right"
            :items="lrOptions"
            :disabled="!locationData.location || isLocationBilateral"
            label="L/R"
            class="col-sm-6"
            :required="isLeftRightRequired && !isLocationBilateral"
          ></auto-complete-box>

          <radio-buttons
            v-if="showDigits"
            v-model="locationData.digits"
            :items="digitOptions"
            :disabled="!locationData.location"
            label="Digits"
            color="error"
            class="col-sm-12"
            columns="6"
            hide-details
            clearable
            :required="$custom.isEmpty(locationData.optional_modifier) ? true : false"
            :is-highlight-empty="$custom.isEmpty(locationData.optional_modifier) ? true : false"
          ></radio-buttons>

          <v-divider
            v-if="showDigits"
          ></v-divider>

          <radio-buttons
            v-if="locationData.location === 32"
            v-model="locationData.finger_toe"
            :items="ftOptions"
            :disabled="!locationData.location"
            class="col-sm-12"
            columns="3"
            hide-details
            clearable
            required
            is-highlight-empty
          ></radio-buttons>
          <radio-buttons
            v-else
            v-model="locationData.dorsal_plantar"
            :items="dpOptions"
            :disabled="!locationData.location"
            class="col-sm-12"
            columns="3"
            hide-details
            clearable
          ></radio-buttons>

          <v-divider></v-divider>

          <radio-buttons
            v-model="locationData.medial_lateral_bilateral"
            :items="!isLocationLeftRight ? mlbOptions : mlOptions"
            :disabled="!locationData.location"
            class="col-sm-12"
            columns="3"
            hide-details
            clearable
            :required="isMlbRequired"
            :is-highlight-empty="isMlbRequired"
          ></radio-buttons>
          <v-divider></v-divider>
          <radio-buttons
            v-model="locationData.anterior_posterior"
            :items="apOptions"
            :disabled="!locationData.location"
            class="col-sm-12"
            columns="3"
            hide-details
            clearable
            :required="isAnteriorPosteriorRequired"
            :is-highlight-empty="isAnteriorPosteriorRequired"
          ></radio-buttons>
          <v-divider></v-divider>
          <radio-buttons
            v-model="locationData.proximal_distal"
            :items="pdOptions"
            :disabled="!locationData.location"
            class="col-sm-12"
            columns="3"
            hide-details
            clearable
          ></radio-buttons>
          <v-divider></v-divider>
          <radio-buttons
            v-model="locationData.inferior_superior"
            :items="isOptions"
            :disabled="!locationData.location"
            class="col-sm-12"
            columns="3"
            clearable
          ></radio-buttons>

          <text-field
            v-model="locationData.optional_modifier"
            label="Optional / Modifier"
            class="col-sm-12"
            maxlength="80"
            counter="80"
          ></text-field>

          <text-area
            v-model="locationData.location_text"
            class="col-sm-12"
            maxlength="300"
            counter="300"
            readonly
          ></text-area>
        </NoteSection>

        <div class="note-section mx-2">
          <v-expand-transition>
            <div v-if="isLocationBilateral">
              <alert color="warning">
                Note!  "Bilateral" is considered too ambiguous for billing purposes.  Please consider revising the stated location or defining multiple wounds instead.
              </alert>
            </div>
          </v-expand-transition>

          <v-expand-transition>
            <div v-if="isLocationBoth">
              <alert color="warning">
                Note!  "Both" is considered too ambiguous for billing purposes.  Please consider revising the stated location or defining multiple wounds instead.
              </alert>
            </div>
          </v-expand-transition>
        </div>

        <v-card-actions>
          <!-- exit -->
          <btn
            label="Exit"
            color="error"
            :icon="icons.mdiClose"
            @click="exit"
          ></btn>

          <v-spacer></v-spacer>

          <!-- clear -->
          <btn
            label="Clear All"
            color="secondary"
            :icon="icons.mdiUndoVariant"
            @click="clear"
          ></btn>

          <v-spacer></v-spacer>

          <!-- save -->
          <btn
            label="Save"
            :icon="icons.mdiCheck"
            :disabled="!formValid"
            @click="save"
          ></btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiCheck,
  mdiClose,
  mdiUndoVariant,
} from '@mdi/js';
import { mapGetters } from 'vuex';

const initialData = {
  wound_id: null,
  left_right: null,
  quadrant: null,
  section: null,
  inferior_superior: null,
  proximal_distal: null,
  anterior_posterior: null,
  medial_lateral_bilateral: null,
  dorsal_plantar: null,
  digits: null,
  finger_toe: null,
  location: null,
  optional_modifier: null,
  location_text: null,
}

export default {
  props: {
    value: {
      type: Object,
      default: null,
    },
    woundId: {
      type: String,
      default: null,
    },
    modalState: {
      type: Boolean,
      default: false,
    },
    /*
      ** Pass in a Wounds array to check for duplicate location_text
      ** inside the provided array.
    */
    wounds: {
      type: Array,
      default: () => []
    },
    wound: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      formValid: false,
      optionalModifierText: null,
      locationData: {
        ...initialData,
        wound_id: this.woundId,
        ...this.$lodash.cloneDeep(this.value),
        section: this.$custom.notEmpty(this.value?.section) ? this.value?.section?.split(',') : null,
      },
      lrOptions: [
        { title: 'Left' },
        { title: 'Right' },
      ],
      dpOptions: [
        { title: 'Dorsal' },
        { title: 'Plantar' },
      ],
      mlbOptions: [
        { title: 'Medial' },
        { title: 'Lateral' },
        { title: 'Bilateral' },
      ],
      mlOptions: [
        { title: 'Medial' },
        { title: 'Lateral' },
      ],
      apOptions: [
        { title: 'Anterior' },
        { title: 'Posterior' },
      ],
      pdOptions: [
        { title: 'Proximal' },
        { title: 'Distal' },
      ],
      isOptions: [
        { title: 'Inferior' },
        { title: 'Superior' },
      ],
      sectionOptions: [
        { title: 'Upper Right Quadrant' },
        { title: 'Epigastric Region' },
        { title: 'Upper Left Quadrant' },
        { title: 'Lower Right Quadrant' },
        { title: 'Umbilical Region' },
        { title: 'Lower Left Quadrant' },
      ],
      quadrantOptions: [
        { title: 'Upper Right Quadrant' },
        { title: 'Upper Left Quadrant' },
        { title: 'Lower Right Quadrant' },
        { title: 'Lower Left Quadrant' },
      ],
      ftOptions: [
        { title: 'Finger' },
        { title: 'Toe' },
      ],
      digitOptions: [
        { title: '1st', value: 1 },
        { title: '2nd', value: 2 },
        { title: '3rd', value: 3 },
        { title: '4th', value: 4 },
        { title: '5th', value: 5 },
        { title: '6th', value: 6 },
      ],
      icons: {
        mdiCheck, mdiUndoVariant, mdiClose,
      },
    }
  },
  computed: {
    ...mapGetters('baseData', ['locations']),
    sortedLocations() {
      // Sort by title asc
      return [...this.locations].sort((a, b) => a.title.localeCompare(b.title))
    },
    showDigits() {
      return this.locationData.location === 18
        || this.locationData.location === 32
        || this.locationData.location === 49
    },
    locationTextLC() {
      return this.$custom.toLowerCase(this.locationData.location_text)
    },
    isLocationBilateral() {
      return this.locationTextLC.includes('bilateral')
        || this.locationTextLC.includes('bi-lateral')
    },
    isLocationLeftRight() {
      return this.locationTextLC.includes('left')
        || this.locationTextLC.includes('right')
    },
    isLocationBoth() {
      return this.locationTextLC === 'both'
        || this.locationTextLC.includes(' both')
        || this.locationTextLC.includes('both ')
    },
    isMlbRequired() {
      return this.locationData.location === 9
        || this.locationData.location === 21
        || this.locationData.location === 28
        || this.locationData.location === 33
        || this.locationData.location === 39
        || this.locationData.location === 55
    },
    isAnteriorPosteriorRequired() {
      return this.locationData.location === 55
        || this.locationData.location === 59
    },
    isLocationLateral() {
      return this.locationTextLC.includes('lateral')
    },
    isLeftRightRequired() {
      return this.locationData.location === 2
        || this.locationData.location === 4
        || this.locationData.location === 5
        || this.locationData.location === 6
        || this.locationData.location === 8
        || this.locationData.location === 12
        || this.locationData.location === 13
        || this.locationData.location === 14
        || this.locationData.location === 15
        || this.locationData.location === 16
        || this.locationData.location === 17
        || this.locationData.location === 18
        || this.locationData.location === 19
        || this.locationData.location === 20
        || this.locationData.location === 22
        || this.locationData.location === 23
        || this.locationData.location === 24
        || this.locationData.location === 26
        || this.locationData.location === 27
        || this.locationData.location === 30
        || this.locationData.location === 31
        || this.locationData.location === 32
        || this.locationData.location === 36
        || this.locationData.location === 40
        || this.locationData.location === 42
        || this.locationData.location === 43
        || this.locationData.location === 44
        || this.locationData.location === 45
        || this.locationData.location === 47
        || this.locationData.location === 48
        || this.locationData.location === 49
        || this.locationData.location === 51
        || this.locationData.location === 52
        || this.locationData.location === 53
        || this.locationData.location === 54
        || this.locationData.location === 55
        || this.locationData.location === 57
        || this.locationData.location === 58
        || this.locationData.location === 59
        || (this.isMlbRequired && this.isLocationLateral)
    },
  },
  watch: {
    modalState() {
      // Scroll to top of modal
      const modalTitle = this.$refs.locationTitle
      if (modalTitle) {
        modalTitle.scrollIntoView(true)
      }
    },
    value() {
      this.locationData = {
        ...initialData,
        wound_id: this.woundId,
        ...this.$lodash.cloneDeep(this.value),
        section: this.$custom.notEmpty(this.value?.section) ? this.value?.section?.split(',') : null,
      }
    },
    locationData: {
      deep: true,
      handler() {
        this.updateLocationText()
      },
    },
  },
  mounted() {
  },
  methods: {
    locationChanged() {
      this.locationData = {
        ...initialData,
        wound_id: this.woundId,
        location: this.locationData.location,
      }
    },
    updateLocationText() {
      const excludedKeys = ['location_text', 'wound_id', 'modified', 'created', 'id_sync']
      let locationTextParts = []
      this.optionalModifierText = ''

      for (const [key, value] of Object.entries(this.locationData)) {
        if (!value || excludedKeys.includes(key)) continue

        switch (key) {
          case 'location':
            const locationName = this.$store.getters['baseData/locationFromId'](value)
            if (locationName) locationTextParts.push(locationName)
            break

          case 'digits':
            locationTextParts.push(this.$custom.toOrdinal(value))
            break

          case 'section':
            if (this.$custom.notEmpty(value)) {
              locationTextParts.push(this.$custom.toListCommaAnd(value, null, 'lc').trim())
            }
            break

          case 'optional_modifier':
            this.optionalModifierText = String(value).trim()
            locationTextParts.push(this.optionalModifierText)
            break

          default:
            locationTextParts.push(String(value).trim())
        }
      }

      const combinedText = locationTextParts.join(' ').trim()
      let properLocationText = this.$custom.toProperCase(combinedText)

      if (this.optionalModifierText) {
        const escaped = this.optionalModifierText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(`\\b${escaped}\\b`, 'i')

        this.locationData.location_text = properLocationText.replace(regex, this.optionalModifierText)
      } else {
        this.locationData.location_text = properLocationText
      }
    },
    exit() {
      this.$root.confirm({
        title: 'Discard Location Changes and Exit?',
        body: 'Are you sure you wish to discard changes and exit?',
        cancel: 'Cancel',
        confirm: 'Discard / Exit',
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          this.$emit('update:modal-state', false)
        }
      })
    },
    clear() {
      this.$root.confirm({
        title: 'Clear All Location Options?',
        body: 'Are you sure you wish to clear all location options?',
        cancel: 'No',
        confirm: 'Yes',
      }).then(result => {
        if (result) {
          this.locationData = {
            ...initialData,
            wound_id: this.woundId,
          }
        }
      })
    },
    save() {
      // Check for duplicate wound location
      if (this.wounds.length && this.locationData.location_text && this.isLocationDuplicated()) {
        this.showDuplicateWoundLocationDialog()
        this.restoreLocation()
        return
      }

      // Join section array into a string if it exists
      if (this.locationData.section) {
        this.locationData.section = this.locationData.section.join()
      }

      // Set modified timestamp
      this.locationData.modified = this.$custom.utcNow()

      // Force Vue to detect change by creating a new object reference
      const updatedLocation = { ...this.locationData }

      // Emit the updated object
      this.$emit('input', updatedLocation)
      this.$emit('update:modal-state', false)
    },
    showDuplicateWoundLocationDialog() {
      this.$root.confirm({
        title: 'A wound already exists in this location.',
        body: `<strong>You entered: <span style="color: #d9534f; font-weight: 600;">${this.locationData.location_text}</span></strong>`,
        confirm: 'Ok',
        html: true,
        cancel: false,
      })
    },
    restoreLocation() {
      this.locationData = {
        ...initialData,
        wound_id: this.woundId,
        ...this.$lodash.cloneDeep(this.value),
        section: this.$custom.notEmpty(this.value?.section) ? this.value?.section?.split(',') : null,
      }
    },
    isLocationDuplicated() {
      const currentPracticeType = this.getPracticeTypeFromWound();
      if (!currentPracticeType) return false;

      return this.wounds.some(
        w => w.id && w.id !== this.woundId
        && w?.location?.location_text?.toLowerCase().trim() === this.locationData?.location_text?.toLowerCase().trim()
        && w.practice_type_id === currentPracticeType
      );
    },
    getPracticeTypeFromWound() {
      return this.wound?.practice_type_id
    },
  },
}
</script>
